import * as Ct from "ldlj"
import { useIntl } from "react-intl"
import {
  AccountRules,
  DeleteRuleThunk,
  Transaction,
} from "../../store/ducks/bank.ducks"
import { RuleCreateParts } from "./RuleCreateParts"
import styled from "styled-components/macro"
import { DateTime } from "luxon"
import { Table } from "../Commons/Table"
import { useRNBSelector } from "../../store/rootReducer"
import { useEffect, useState, Fragment } from "react"
import { colors } from "../../styles/design.config"
import { displayRule } from "../../utils/bank"
import { ReactComponent as Trash } from "../../assets/drop-documents/TrashCan.svg"
import { ReactComponent as Pencil } from "../../assets/design-pencil.svg"
import { ReactComponent as Plus } from "../../assets/single-plus.svg"
import { ReactComponent as Info } from "../../assets/info-line.svg"
import { DeleteRuleModal } from "./DeleteRuleModal"
import { useDispatch } from "react-redux"
import { getIdFromParams } from "../../utils/company"
import { useParams } from "react-router-dom"
import { Alert } from "../Commons/Alert"
import { Text } from "../Commons/Text"

interface DisplayTransactionProps {
  isDisplayed: boolean
  onClose: () => void
  transaction: Transaction
}

export const DisplayTransactionModal = ({
  isDisplayed,
  onClose,
  transaction,
}: DisplayTransactionProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const [possibleRules, setPossibleRules] = useState<AccountRules[]>([])
  const [deleteRule, setDeleteRule] = useState<AccountRules | null>(null)
  const [selectedTab, setSelectedTab] = useState<
    "createForOneTransaction" | "create"
  >("createForOneTransaction")
  const [currentTransaction, setCurrentTransaction] =
    useState<Transaction>(transaction)

  const rules = useRNBSelector((state) => state.bank.dataToCreateRule.rules)
  const accountID = getIdFromParams(useParams())("account_id")
  const accounts = useRNBSelector((state) => state.bank.accounts)
  const currentAcccount = Object.values(accounts).find(
    (a) => a.id === accountID
  )

  useEffect(() => {
    if (
      Object.values(accounts).length > 0 &&
      currentAcccount &&
      currentAcccount.transactions &&
      currentAcccount.transactions.length > 0
    ) {
      const transactionChanged = currentAcccount.transactions.find(
        (t) => t.id === transaction.id
      )
      if (transactionChanged) {
        setCurrentTransaction(transactionChanged)
      }
    }
  }, [currentAcccount?.transactions])

  useEffect(() => {
    if (
      currentTransaction.possible_rules &&
      currentTransaction.possible_rules.length > 0
    ) {
      setPossibleRules(
        rules.filter(
          (r) =>
            currentTransaction.possible_rules?.includes(r.id) &&
            r.priority_number
        )
      )
    }
  }, [rules, currentTransaction, currentAcccount?.transactions])

  const ruleOnlyForThisTransaction =
    transaction.rule_id &&
    rules.find((r) => r.id === transaction.rule_id) !== undefined &&
    rules.find((r) => r.id === transaction.rule_id)?.transaction_id ===
      transaction.id
      ? rules.find((r) => r.id === transaction.rule_id)
      : null

  const isRuleOnlyForThisTransaction = Boolean(ruleOnlyForThisTransaction)

  const columns = [
    {
      headerText: "bank-management.transaction.table-header.date",
      content: (row: Transaction) => (
        <>
          <Ct.Spacer width={2} height={0} />
          <ColumnRow>
            <Row
              text={DateTime.fromJSDate(new Date(row.date)).toFormat(
                "dd/MM/yyyy"
              )}
            />
            {row.second_row_vat && (
              <Row
                text={DateTime.fromJSDate(new Date(row.date)).toFormat(
                  "dd/MM/yyyy"
                )}
              />
            )}
            <Row
              text={DateTime.fromJSDate(new Date(row.date)).toFormat(
                "dd/MM/yyyy"
              )}
            />
          </ColumnRow>
        </>
      ),
    },
    {
      headerText: "bank-management.transaction.table-header.label",
      content: (row: Transaction) => (
        <ColumnRow>
          <Row text={row.description} />
          {row.second_row_vat && <Row text={row.description} />}
          <Row text={row.description} />
        </ColumnRow>
      ),
    },
    {
      headerText: "bank-management.transaction.table-header.debit",
      content: (row: Transaction) => {
        const empty = intl.formatMessage({
          id: "bank-management.transaction.table-body.empty-space",
        })

        const firstRow = row.first_row?.debit ? row.first_row.debit : empty
        const secondRow =
          row.second_row_vat && row.second_row_vat?.debit
            ? row.second_row_vat.debit
            : empty
        const thirdRow = row.third_row?.debit ? row.third_row?.debit : empty
        return (
          <ColumnRow>
            <Row text={firstRow} />
            {row.second_row_vat && <Row text={secondRow} />}
            <Row text={thirdRow} />
          </ColumnRow>
        )
      },
    },
    {
      headerText: "bank-management.transaction.table-header.credit",
      content: (row: Transaction) => {
        const empty = intl.formatMessage({
          id: "bank-management.transaction.table-body.empty-space",
        })

        const firstRow = row.first_row?.credit ? row.first_row.credit : empty
        const secondRow =
          row.second_row_vat && row.second_row_vat?.credit
            ? row.second_row_vat.credit
            : empty
        const thirdRow = row.third_row?.credit ? row.third_row?.credit : empty
        return (
          <ColumnRow>
            <Row text={firstRow} />
            {row.second_row_vat && <Row text={secondRow} />}
            <Row text={thirdRow} />
          </ColumnRow>
        )
      },
    },
    {
      headerText: "bank-management.transaction.table-header.assignment",
      content: (row: Transaction) => {
        return (
          <ColumnRow>
            <Row text={row.first_row?.assignment} />
            {row.second_row_vat && <Row text={row.second_row_vat.assignment} />}
            <Row text={row.third_row?.assignment} />
          </ColumnRow>
        )
      },
    },
  ]

  return (
    <Ct.Modal
      isDisplayed={isDisplayed}
      onClose={() => {
        onClose()
      }}
      left="25%"
      right="25%"
      top="10rem"
    >
      <Ct.Card width={"155rem"}>
        <Ct.CloseCross onClick={onClose} />

        <Ct.Text
          text={intl.formatMessage({
            id: `bank-management.transaction.detail.title`,
          })}
          textStyle={{
            fontWeight: 700,
            fontSize: 5,
            fontFamily: "Poppins",
          }}
        />

        <Ct.Separator size="full" />
        <Ct.Spacer height={2} />
        <Wrapper>
          <TableWrapper>
            <Table
              intl={intl}
              columns={columns}
              rows={[currentTransaction]}
              alignItems={"center"}
              width={"98%"}
              height={"100%"}
              padding={"0 1rem"}
              paddingHeader={"0rem 0rem 0rem 2rem"}
              paddingRows={"0px 0px 8px 0px"}
              heightHeader={"5rem"}
              fontWeightTitle={600}
              keyBuilder={(rowData) => String(rowData.id)}
              customScrollBar={true}
            />
          </TableWrapper>

          <Ct.Spacer height={2} />

          <Rules>
            <Ct.Text
              text={intl.formatMessage({
                id: `bank-management.transaction.detail.rules.title`,
              })}
              textStyle={{
                color: "navy",
                fontWeight: 700,
                fontSize: 2,
                textTransform: "uppercase",
              }}
            />
          </Rules>

          <Ct.Spacer height={2} />

          <RulesWrapperOverFlow>
            {possibleRules.length > 0 ? (
              <>
                {possibleRules.map((rule) => (
                  <Fragment key={rule.priority_number}>
                    <RuleCard
                      width={"100%"}
                      disabled={isRuleOnlyForThisTransaction}
                    >
                      <Ct.Row>
                        <Ct.Spacer width={2} />
                        <Ct.Text
                          text={"#" + rule.priority_number}
                          textStyle={{
                            color: isRuleOnlyForThisTransaction
                              ? "moon"
                              : "cornflower",
                            fontWeight: 700,
                            fontSize: 2,
                          }}
                        />
                        <Ct.Spacer width={4} />

                        <Ct.Text
                          text={intl.formatMessage({
                            id: "bank-management.rules.rule-text",
                          })}
                          textStyle={{
                            color: isRuleOnlyForThisTransaction
                              ? "moon"
                              : "navy",
                            fontSize: 1.75,
                          }}
                        />
                        <Ct.Text
                          text={displayRule(rule)}
                          textStyle={{
                            color: isRuleOnlyForThisTransaction
                              ? "moon"
                              : "navy",
                            fontSize: 1.75,
                            fontWeight: 600,
                          }}
                        />
                      </Ct.Row>

                      {isRuleOnlyForThisTransaction ? (
                        <RuleActions>
                          <Info />
                          <Ct.Spacer width={2} />
                          <Ct.Text
                            text={intl.formatMessage({
                              id: "bank-management.transaction.detail.rules.not-applied",
                            })}
                            textStyle={{
                              color: "steelBlue",
                              fontSize: 1.75,
                              fontStyle: "italic",
                            }}
                          />
                        </RuleActions>
                      ) : (
                        <RuleActions>
                          <Action
                            size={"3rem"}
                            onClick={() => {
                              setDeleteRule(rule)
                            }}
                          >
                            <Trash />
                          </Action>
                          <Ct.Spacer width={3} />
                        </RuleActions>
                      )}
                    </RuleCard>
                    <Ct.Spacer height={2} />
                  </Fragment>
                ))}
              </>
            ) : (
              <WrapperAlert>
                <Ct.Spacer height={3} />
                <Alert alertType="info">
                  <Text
                    text={intl.formatMessage({
                      id: "bank-management.transaction.detail.rules.empty",
                    })}
                  />
                </Alert>
              </WrapperAlert>
            )}
          </RulesWrapperOverFlow>

          <Ct.Spacer height={4} />

          <Tabs>
            <Part
              selected={selectedTab === "createForOneTransaction"}
              onClick={() => setSelectedTab("createForOneTransaction")}
            >
              <Pencil />
              <Ct.Spacer />
              <Ct.Text
                text={intl.formatMessage({
                  id: `bank-management.transaction.detail.rules.edit-line`,
                })}
                textStyle={{
                  color:
                    selectedTab === "createForOneTransaction"
                      ? "white"
                      : "navy",
                  fontWeight: 700,
                  fontSize: 2,
                  cursor: "pointer",
                }}
              />
            </Part>

            <Ct.Spacer />

            <Part
              selected={selectedTab === "create"}
              onClick={() => setSelectedTab("create")}
            >
              <Plus />
              <Ct.Spacer />
              <Ct.Text
                text={intl.formatMessage({
                  id: `bank-management.transaction.detail.rules.add-line`,
                })}
                textStyle={{
                  color: selectedTab === "create" ? "white" : "navy",
                  fontWeight: 700,
                  fontSize: 2,
                  cursor: "pointer",
                }}
              />
            </Part>
          </Tabs>

          <ContentTab>
            <Ct.Spacer height={2} />
            <RuleCreateParts
              isDisplayed={isDisplayed}
              onClose={onClose}
              rulesLength={rules.length + 1}
              createOrEdit={
                selectedTab === "createForOneTransaction"
                  ? "createForOneTransaction"
                  : "create"
              }
              ruleToEdit={ruleOnlyForThisTransaction || null}
              createForOneTransaction={transaction}
              width={"100%"}
              from={"transactions"}
              selectedTab={selectedTab}
            />
          </ContentTab>
        </Wrapper>

        {deleteRule && (
          <DeleteRuleModal
            isDisplayed={!!deleteRule}
            onClose={() => {
              setDeleteRule(null)
            }}
            rule={deleteRule}
            deleteRule={() => {
              dispatch(
                DeleteRuleThunk(accountID || 0, deleteRule.id, "transactions")
              )
              setDeleteRule(null)
            }}
          />
        )}
      </Ct.Card>
    </Ct.Modal>
  )
}

const ColumnRow = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`
const Row = styled((props) => <Ct.Text {...props} />)`
  display: flex;
  align-items: center;
  padding-top: 2rem;
  cursor: pointer;
`
const TableWrapper = styled.div`
  overflow-y: auto;
  border-radius: 2.5rem;
  margin: 0 0 4rem 0rem;
  width: 100%;
`
const Rules = styled.div`
  align-items: flex-start;
  width: 100%;
`
const RulesWrapperOverFlow = styled.div`
  overflow: auto;
  height: 25rem;
  width: 100%;
`
const RuleCard = styled(Ct.Card)<{ disabled: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  padding: 2rem 4rem;
  background-color: ${({ disabled }) =>
    disabled ? `${colors.desertStorm}` : `${colors.white}`};
`

const RuleActions = styled.div`
  display: flex;
  align-items: center;
`

const Action = styled.span<{ size: string; asc?: boolean }>`
  width: ${({ size }) => `${size}`};
  height: ${({ size }) => `${size}`};
  cursor: pointer;
  & path {
    fill: ${({ asc }) =>
      asc === false || asc === null
        ? `${colors.lavender}`
        : `${colors.cornflower}`};
  }
`
const Wrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  max-height: 75vh;
`
const WrapperAlert = styled.div`
  width: 50%;
  margin: 0 auto;
`
const Tabs = styled.div`
  display: flex;
  justify-content: space-between;
  width: 98%;
  margin: 0 1rem;
  border-bottom: 1px solid ${colors.navy};
`

const Part = styled.div<{ selected: boolean }>`
  display: flex;
  cursor: pointer;
  color: ${({ selected }) => (selected ? `${colors.white}` : `${colors.navy}`)};
  & path {
    fill: ${({ selected }) =>
      selected ? `${colors.white}` : `${colors.navy}`};
  }
  background-color: ${({ selected }) =>
    selected ? `${colors.navy}` : `${colors.white}`};
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  border-radius: 1rem 1rem 0 0;
  box-shadow: 0px -5px 10px rgba(2, 76, 248, 0.15);
`

const ContentTab = styled.div`
  border: 1px solid ${colors.lavender};
  margin: 0rem 0rem 1rem 1rem;
  width: 96%;
  padding: 1rem 1.6rem 1rem 1rem;
  border-radius: 0 0 1rem 1rem;
`
